import React, { useCallback, useMemo, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { ExternalLink } from '@labrav/react-components'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { API } from '../../../common/api'
import { labravAWSEngineServiceUrl } from '../../../common/utils/constants'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { useDispatch } from '../../../store/index'
import { useStyles } from './DealPropertyChangePicker.styles'

type PickerReferral = {
  id: string
  label: string
  account_id?: string
  accountId?: string
  cloud_type?: string
  cloudType?: string
  object_type?: string
  objectType?: string
  view_url?: string
  viewUrl?: string
}

const DEFAULT_APPLY_ERROR_MESSAGE = 'Failed to apply deal property change.'

/**
 * Notify the HubSpot parent frame to close this iframe modal.
 * Target origin is taken from document.referrer (the embedding HubSpot page)
 * rather than '*' to avoid leaking the close signal to unexpected parents.
 */
const closeHubspotIframeModal = (action: 'CANCEL' | 'DONE') => {
  const payload = JSON.stringify({ action })

  if (typeof document === 'undefined' || !document.referrer) {
    console.error(
      'Cannot determine parent origin - referrer is not available; skipping postMessage'
    )
    return
  }

  let targetOrigin: string
  try {
    targetOrigin = new URL(document.referrer).origin
  } catch {
    console.error('Invalid document.referrer URL:', document.referrer)
    return
  }

  window.parent.postMessage(payload, targetOrigin)
  window.top?.postMessage(payload, targetOrigin)
}

const parseJsonParam = <T,>(value: string | null, fallback: T): T => {
  if (!value?.trim()) return fallback
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

const patchReferralLabraFields = async (
  partnerId: string,
  referralId: string,
  labraReferral: Record<string, string>
) => {
  const endpointURL = `${labravAWSEngineServiceUrl}/partners/${partnerId}/referrals/${encodeURIComponent(referralId)}`
  await API.patch(endpointURL, { labra_referral: labraReferral })
}

const getReferralAccountId = (referral: PickerReferral) =>
  referral.accountId?.trim() || referral.account_id?.trim() || ''

const normalizeReferralCloudType = (cloud?: string) => {
  const normalized = cloud?.trim().toLowerCase()
  if (!normalized) return ''
  if (normalized === 'microsoft') return 'azure'
  return normalized
}

const getReferralCloudType = (referral: PickerReferral) =>
  normalizeReferralCloudType(referral.cloudType || referral.cloud_type)

const getReferralObjectType = (referral: PickerReferral) => {
  const objectType = (referral.objectType || referral.object_type || 'opportunity')
    .trim()
    .toLowerCase()
  return objectType === 'lead' ? 'lead' : 'opportunity'
}

const buildReferralViewUrl = ({
  referral,
  secureKey,
  portalId,
  search,
}: {
  referral: PickerReferral
  secureKey: string
  portalId: string
  search: string
}): string | null => {
  const directUrl = referral.viewUrl?.trim() || referral.view_url?.trim()
  if (directUrl) return directUrl

  const referralId = referral.id?.trim()
  const accountId = getReferralAccountId(referral)
  const cloudType = getReferralCloudType(referral)
  const objectType = getReferralObjectType(referral)

  if (!secureKey || !portalId || !referralId || !accountId || !cloudType) {
    return null
  }

  const pathname = `/external-apps/hubspot/v1/${encodeURIComponent(secureKey)}/portal/${encodeURIComponent(portalId)}/${encodeURIComponent(cloudType)}/${encodeURIComponent(accountId)}/referral/${encodeURIComponent(referralId)}/${objectType}`
  const normalizedSearch = search.startsWith('?') ? search : search ? `?${search}` : ''

  return `${window.location.origin}${pathname}${normalizedSearch}`
}

const DealPropertyChangePicker: React.FC = () => {
  const { classes, cx } = useStyles()
  const { secureKey = '', partnerId = '' } = useParams<{
    secureKey: string
    partnerId: string
  }>()
  const { search } = useLocation()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const portalId =
    searchParams.get('portal_id')?.trim() || searchParams.get('portalId')?.trim() || ''
  const referrals = useMemo(
    () =>
      parseJsonParam<PickerReferral[]>(searchParams.get('referrals'), []).filter(
        referral => referral.id?.trim()
      ),
    [searchParams]
  )
  const labraReferralPatch = useMemo(
    () => parseJsonParam<Record<string, string>>(searchParams.get('patch'), {}),
    [searchParams]
  )
  const applyErrorMessage =
    searchParams.get('applyErrorMessage')?.trim() || DEFAULT_APPLY_ERROR_MESSAGE

  const [selectedReferralIds, setSelectedReferralIds] = useState<string[]>([])
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null)
  const [feedbackIsError, setFeedbackIsError] = useState(false)
  const [isApplying, setIsApplying] = useState(false)

  const toggleReferral = useCallback((referralId: string, checked: boolean) => {
    setSelectedReferralIds(current => {
      if (checked) {
        return current.includes(referralId) ? current : [...current, referralId]
      }
      return current.filter(id => id !== referralId)
    })
  }, [])

  const handleCancel = useCallback(() => {
    closeHubspotIframeModal('CANCEL')
  }, [])

  const handleApply = useCallback(async () => {
    if (!selectedReferralIds.length) {
      setFeedbackMessage('Select at least one referral.')
      setFeedbackIsError(true)
      return
    }

    if (!partnerId.trim()) {
      setFeedbackMessage('Missing partner context.')
      setFeedbackIsError(true)
      return
    }

    if (!Object.keys(labraReferralPatch).length) {
      setFeedbackMessage('Missing referral update payload.')
      setFeedbackIsError(true)
      return
    }

    setFeedbackMessage(null)
    setFeedbackIsError(false)
    setIsApplying(true)

    const selectedIds = [
      ...new Set(selectedReferralIds.map(id => id.trim()).filter(Boolean)),
    ]
    const labelById = new Map(
      referrals.map(referral => [
        referral.id,
        referral.label?.trim() || referral.id,
      ]),
    )

    const successes: string[] = []
    const failures: { referralLabel: string; errorMessage: string }[] = []
    const trimmedPartnerId = partnerId.trim()

    const results = await Promise.allSettled(
      selectedIds.map(id =>
        patchReferralLabraFields(trimmedPartnerId, id, labraReferralPatch)
      )
    )

    results.forEach((result, index) => {
      const referralId = selectedIds[index]
      const referralLabel = labelById.get(referralId) || referralId

      if (result.status === 'fulfilled') {
        successes.push(referralLabel)
        return
      }

      const error = result.reason
      failures.push({
        referralLabel,
        errorMessage:
          error instanceof Error
            ? error.message
            : `Failed to update referral ${referralId}`,
      })
    })

    if (successes.length) {
      dispatch(
        updateAppAlert({
          message:
            successes.length === 1
              ? `Updated ${successes[0]} successfully.`
              : `Updated ${successes.length} referrals successfully: ${successes.join(', ')}`,
          messageType: 'SUCCESS',
          autoClose: true,
        }),
      )
    }

    if (failures.length) {
      const failureMessage = failures
        .map(f => `${f.referralLabel}: ${f.errorMessage}`)
        .join(' | ')

      const dispatchError = () =>
        dispatch(
          updateAppAlert({
            message: failureMessage || applyErrorMessage,
            messageType: 'ERROR',
            autoClose: true,
          }),
        )

      // Let the success snackbar render first (if any), then surface failures separately.
      if (successes.length) {
        setTimeout(dispatchError, 300)
      } else {
        dispatchError()
      }

      setIsApplying(false)
      return
    }

    closeHubspotIframeModal('DONE')
  }, [
    applyErrorMessage,
    dispatch,
    labraReferralPatch,
    partnerId,
    referrals,
    selectedReferralIds,
  ])

  return (
    <Box className={classes.shell} data-testid="deal-property-change-picker">
      <Box className={classes.scrollableBody}>
        {referrals.length ? (
          <Box className={classes.referralList}>
            {referrals.map(referral => {
              const referralViewUrl = buildReferralViewUrl({
                referral,
                secureKey,
                portalId,
                search,
              })

              return (
                <Box key={referral.id} className={classes.referralRow}>
                  <FormControlLabel
                    className={classes.referralCheckbox}
                    control={
                      <Checkbox
                        checked={selectedReferralIds.includes(referral.id)}
                        onChange={event =>
                          toggleReferral(referral.id, event.target.checked)
                        }
                        size="medium"
                      />
                    }
                    label={
                      <Typography className={classes.referralLabel}>
                        {referral.label?.trim() || referral.id}
                      </Typography>
                    }
                  />
                  {referralViewUrl ? (
                    <ExternalLink
                      href={referralViewUrl}
                      noopener
                      noreferrer
                      className={classes.externalLink}
                      iconClassName={classes.externalLinkIcon}
                    >
                      <Box component="span" className={classes.externalLinkHiddenLabel}>
                        Open {referral.label?.trim() || referral.id} in a new tab
                      </Box>
                    </ExternalLink>
                  ) : null}
                </Box>
              )
            })}
          </Box>
        ) : (
          <Typography className={classes.emptyState}>
            No active referrals are available for this deal.
          </Typography>
        )}
      </Box>

      <Box className={classes.footer}>
        {feedbackMessage ? (
          <Box
            className={cx(
              classes.feedback,
              feedbackIsError ? classes.feedbackError : classes.feedbackSuccess
            )}
          >
            {feedbackMessage}
          </Box>
        ) : null}

        <Box className={classes.actions}>
          <Button
            variant="contained"
            className={classes.applyBtn}
            disabled={!referrals.length || isApplying}
            onClick={() => void handleApply()}
          >
            {isApplying ? 'Applying...' : 'Apply'}
          </Button>
          <Button
            variant="outlined"
            className={classes.cancelBtn}
            disabled={isApplying}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default DealPropertyChangePicker
